import React, { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { Namespaces } from 'types/locales';
import InternalLinksContainer, { LinkDataType } from './internalLinksContainer';
import type { LocalizedLinkData } from 'components/pages/area-listings/generate-dynamic-footer-data';

export type LocalizedInternalLinksProps = {
  /** Localized link data with translation keys */
  data: readonly LocalizedLinkData[];
  /** Whether sections should be expanded by default */
  isActive: boolean;
  /** Heading for the entire section */
  heading: string;
  /** Whether to show column separators between groups */
  showGroupSeparators?: boolean;
  /** Whether sections are collapsible */
  isCollapsable?: boolean;
};

/**
 * LocalizedInternalLinks component that translates footer data on the client side.
 * 
 * This component takes LocalizedLinkData (which contains translation keys and parameters)
 * and translates them using the internalLinks namespace before rendering.
 * 
 * Benefits:
 * - Supports multiple languages through i18next
 * - Translation keys are type-safe (validated at build time)
 * - Separates data generation (server) from presentation (client)
 * - Maintains backward compatibility with fallback strings
 * 
 * @example
 * ```tsx
 * const footerData = await generateLocalizedCityFooterData(...);
 * return <LocalizedInternalLinks data={footerData} isActive={true} />;
 * ```
 */
export default function LocalizedInternalLinks({
  data,
  isActive,
  heading,
  showGroupSeparators = false,
  isCollapsable = false,
}: LocalizedInternalLinksProps) {
  const { t } = useTranslation(Namespaces.INTERNAL_LINKS);

  // Transform LocalizedLinkData to LinkDataType by applying translations
  const translatedData: LinkDataType[] = useMemo(() => {
    return data.map(section => ({
      title: section.titleKey
        ? t(section.titleKey, {
          ...section.titleParams,
          defaultValue: section.title || '',
        })
        : section.title || '',
      links: section.links.map(link => ({
        label: link.labelKey
          ? t(link.labelKey, {
            ...link.labelParams,
            defaultValue: link.label || '',
          })
          : link.label || '',
        link: link.link,
      })),
    }));
  }, [data, t]);

  return (
    <InternalLinksContainer
      data={translatedData}
      isActive={isActive}
      heading={heading}
      showGroupSeparators={showGroupSeparators}
      isCollapsable={isCollapsable}
    />
  );
}
